import { alpha, Box, useTheme } from "@mui/material";
import {
  ChartLegendItem,
  getChartColor,
  useFormatMetric,
} from "features/analytics";
import { IStyledChartProps } from "Interfaces";
import { snakeToTitle } from "Helpers/camelToText";
import { formatEnumVal } from "Helpers/enumToText";
import { truncate } from "Helpers/truncate";
import { useAnalyticsContext } from "Providers/AnalyticsProvider";
import { useMemo, useState } from "react";
import {
  Cell,
  Label,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Sector,
} from "recharts";
import NoData from "Components/NoData";
import { ApiMetric } from "@incendium/api";

interface IStyledDoughnutChart extends IStyledChartProps {
  label?: { title: string; value: string | number };
  dataKey?: string;
  noShape?: boolean;
  showTotalText?: string;
  totalFontSize?: number;
  formatTotalValue?: (n: number) => string;
  total?: number;
  comparismentTotal?: number;
}

const renderActiveShape = (formatMetric, comparismentData) => (props: any) => {
  const {
    cx,
    cy,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
    value,
    tooltipPayload,
    name,
  } = props;

  const found = (comparismentData || []).find((d) => d.name === name);
  const charWidth = 12;
  const chars = Math.floor(cx / charWidth);

  return (
    <g>
      <text
        x={cx}
        y={cy}
        dy={-4}
        fill={fill}
        fontWeight="bold"
        fontSize={chars > 7 ? 16 : 12}
        textAnchor="middle"
      >
        {truncate(formatEnumVal(payload.name), chars)}
      </text>
      <text
        x={cx}
        y={cy}
        dy={chars > 7 ? 16 : 14}
        fontSize={chars > 7 ? 20 : 16}
        textAnchor="middle"
        fontWeight="bold"
        fill={fill}
      >
        <tspan>
          {`${formatMetric(tooltipPayload[0].dataKey || "", value)}`}
        </tspan>
        {!!found && (
          <tspan fill={alpha(fill, 0.9)} fontSize={12}>
            {" "}
            (
            {`${formatMetric(
              tooltipPayload[0].dataKey || "",
              Number(found[tooltipPayload[0].dataKey] || 0)
            )}`}
            )
          </tspan>
        )}
      </text>

      <text
        x={cx}
        y={cy}
        dy={34}
        textAnchor={"middle"}
        fill="#999"
        fontSize={12}
      >
        {`(${(percent * 100).toFixed(2)}%)`}
      </text>

      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 6}
        outerRadius={outerRadius + 10}
        fill={fill}
      />
    </g>
  );
};

const CustomLabel = ({
  viewBox,
  title,
  value,
  fontSize,
  comparismentTotal,
  ...rest
}: any) => {
  const theme = useTheme();

  const { cx, cy } = viewBox;
  const charWidth = 9;
  const chars = Math.floor(cx / charWidth);

  const smallerFont = 20;
  const smallestFont = 16;

  const fontSizeCalc = cy > 150 ? fontSize : fontSize / 1.2;

  return (
    <text
      x={cx}
      y={cy}
      fill={theme.palette.primary.dark}
      textAnchor="middle"
      dominantBaseline="middle"
    >
      <tspan
        alignmentBaseline="middle"
        fontSize={cy > 150 ? smallerFont : smallestFont}
        x={cx}
        y={cy - (cy > 150 ? smallerFont : smallestFont) / 2 - 2}
        fontWeight={"bold"}
      >
        {truncate(title, chars)}
      </tspan>
      <tspan
        fontSize={fontSizeCalc}
        fontWeight={"bold"}
        x={cx}
        y={cy + fontSizeCalc / 2 + 2}
        fill={theme.palette.primary.dark}
      >
        <>{value}</>
      </tspan>
      {!!comparismentTotal && (
        <tspan
          fontSize={smallestFont}
          fontWeight={"bold"}
          x={cx}
          y={cy + (fontSizeCalc / 2 + 2) * 3}
          fill={theme.palette.primary.light}
        >
          <>({comparismentTotal})</>
        </tspan>
      )}
    </text>
  );
};

function StyledDoughnutChart({
  height,
  width,
  data,
  comparismentChart,
  children,
  label,
  dataKey,
  noAnimation,
  noLegend,
  noShape,
  legendCols = 3,
  fills,
  showTotalText = "Total",
  totalFontSize = 26,
  formatTotalValue,
  total,
  comparismentTotal,
}: IStyledDoughnutChart) {
  const theme = useTheme();
  const chartData = useMemo(() => data.slice(), [data]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const { colourMap } = useAnalyticsContext();
  const formatMetric = useFormatMetric();

  const calcTotal = useMemo(() => {
    return chartData.reduce((agg, d) => {
      return agg + Number(d[dataKey || "value"]);
    }, 0);
  }, [chartData, dataKey]);

  const showComparison = useMemo(() => {
    return !!comparismentChart && !!comparismentChart.data;
  }, [comparismentChart]);

  if (calcTotal === 0) {
    return <NoData />;
  }

  return (
    <ResponsiveContainer
      height={height || "100%"}
      width={width || "100%"}
      aspect={1}
    >
      <PieChart>
        <Pie
          dataKey={dataKey || "value"}
          data={chartData}
          innerRadius={showComparison ? "75%" : "70%"}
          outerRadius={"90%"}
          startAngle={-270}
          endAngle={-630}
          fill={theme.palette.secondary.main}
          isAnimationActive={!noAnimation}
          activeIndex={activeIndex}
          activeShape={
            noShape
              ? undefined
              : renderActiveShape(formatMetric, comparismentChart?.data)
          }
          onMouseEnter={(_, index) => setActiveIndex(index)}
          onMouseLeave={() => setActiveIndex(-1)}
        >
          {chartData.map((k, index) => {
            const c =
              colourMap && colourMap[k.name]
                ? colourMap[k.name]
                : fills && fills[k.name]
                ? fills[k.name]
                : getChartColor(index, theme.palette.charts);

            return (
              <Cell
                key={`cell-${index}`}
                strokeWidth={0}
                fill={c || theme.palette.secondary.main}
              />
            );
          })}
          {label && activeIndex === -1 && (
            <Label
              position="center"
              content={
                <CustomLabel
                  title={label.title}
                  value={label.value}
                  fontSize={totalFontSize}
                />
              }
            />
          )}
          {total && activeIndex === -1 && (
            <Label
              position="center"
              content={
                <CustomLabel
                  title={
                    showTotalText && showTotalText !== ""
                      ? showTotalText
                      : "Total"
                  }
                  value={
                    formatTotalValue
                      ? formatTotalValue(total || calcTotal)
                      : formatMetric(dataKey as ApiMetric, total || calcTotal)
                  }
                  fontSize={
                    totalFontSize && totalFontSize !== 0 ? totalFontSize : 26
                  }
                  comparismentTotal={
                    comparismentTotal
                      ? formatTotalValue
                        ? formatTotalValue(comparismentTotal)
                        : formatMetric(dataKey as ApiMetric, comparismentTotal)
                      : undefined
                  }
                />
              }
            />
          )}
        </Pie>
        {/* Inner pie for comparison data */}
        {showComparison && (
          <Pie
            dataKey={dataKey || "value"}
            data={comparismentChart!.data}
            innerRadius={"65%"}
            outerRadius={"74%"}
            startAngle={-270}
            endAngle={-630}
            fill={theme.palette.primary.light}
            isAnimationActive={!noAnimation}
          >
            {chartData.map((k, index) => {
              const c =
                colourMap && colourMap[k.name]
                  ? colourMap[k.name]
                  : fills && fills[k.name]
                  ? fills[k.name]
                  : getChartColor(index, theme.palette.charts);

              return (
                <Cell
                  key={`cell-compare-${index}`}
                  strokeWidth={0}
                  fill={alpha(c || theme.palette.secondary.main, 0.8)}
                />
              );
            })}
          </Pie>
        )}
        {children}
        {!noLegend && (
          <Legend
            verticalAlign="bottom"
            align="center"
            content={({ payload }) => (
              <Box
                mt={2}
                mb={2}
                sx={{
                  display: "grid",
                  placeItems: "center",
                  gridTemplateColumns: `repeat(${
                    (payload || []).length > legendCols
                      ? legendCols
                      : (payload || []).length
                  }, 1fr)`,
                }}
              >
                {(payload || []).map((entry, i) => (
                  <ChartLegendItem
                    fill={
                      colourMap && colourMap[entry.value]
                        ? colourMap[entry.value]
                        : undefined
                    }
                    item={snakeToTitle(entry.value.replace("\\", "_"))}
                    index={i}
                    key={i}
                  />
                ))}
              </Box>
            )}
          />
        )}
      </PieChart>
    </ResponsiveContainer>
  );
}

export default StyledDoughnutChart;
